/**
 * RM AI chat: assistant text → the HTML AgentChatMessage renders with v-html.
 * Moved here from the component so it can be tested directly (RM-349 review,
 * 9/30): the replies table lets RM edit wording, so wording reaches this
 * renderer from an admin page, and escapeHtml didn't escape quotes — a link
 * like [x](/a" onmouseover="…) broke out of its href. Quotes are escaped now,
 * so every attribute value built below stays inside its quotes.
 */

export const escapeHtml = text => String(text)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;')

export const linkifyUrls = text => {
  // A bare URL ends at a quote — now an entity, since escapeHtml escapes quotes.
  const urlRegex = /((?:https?:\/\/|www\.)(?:(?!&quot;|&#39;)[^\s<>"'])+)/gi

  // Split out existing <a>...</a> blocks so we don't double-linkify URLs that
  // are already inside an anchor (either in an href attribute or as link text).
  const parts = text.split(/(<a\b[^>]*>[\s\S]*?<\/a>)/gi)

  return parts.map(part => {
    if (/^<a\b/i.test(part)) return part

    return part.replace(urlRegex, rawMatch => {
      let url = rawMatch
      let trailing = ''

      while (/[),.!?;:]$/.test(url)) {
        trailing = `${url.slice(-1)}${trailing}`
        url = url.slice(0, -1)
      }

      const href = url.startsWith('www.') ? `https://${url}` : url

      return `<a href="${href}" target="_blank" rel="noopener noreferrer">${url}</a>${trailing}`
    })
  }).join('')
}

// Markdown-to-HTML for a chunk of assistant text (bold, italic, lists, links).
/**
 * A {{small:…}} line written straight after a restaurant row is that row's own
 * line: "Booked 10+ times, incl last 3mo" under one of the member's Favorites
 * (RM-396). The chat draws it inside the row, where the Favorites page shows
 * it, instead of as a loose line between two restaurants.
 *
 * @param {string} text - the text that follows a restaurant row's token
 * @returns {{ line: string, rest: string }} `line` is '' when the text doesn't open with one
 */
export const takeRowLine = text => {
  const raw = String(text || '').trim()
  const match = raw.match(/^\{\{small:([^}]+)\}\}\s*/)
  if (!match) return { line: '', rest: raw }

  return { line: match[1].trim(), rest: raw.slice(match[0].length).trim() }
}

/**
 * Every {{small:…}} line that opens the text after a restaurant row, in order:
 * a Discovery card's facts ("Members have booked a party of 24", the spaces and
 * their sizes) come one token per line under its card (RM-399; Kapil, 10/22),
 * and the chat draws them all inside the row - never as body text between two
 * cards. Stops at the first line that is not one.
 *
 * @param {string} text - the text that follows a restaurant row's token
 * @returns {{ lines: string[], rest: string }} `lines` is [] when the text doesn't open with one
 */
export const takeRowLines = text => {
  const lines = []
  let { line, rest } = takeRowLine(text)
  while (line) {
    lines.push(line);
    ({ line, rest } = takeRowLine(rest))
  }

  return { lines, rest }
}

// The category headers RM AI's Discovery page carries in the app, one per bold line
// (lib/agent/replyCatalog, the Discovery capability). An app-channel page is known by
// them; a texted page (plain "Trending on RM:" lines over {{discovery-row:…}} tokens) is not.
const PAGE_HEADERS = "Booked via RM(?: - past (?:3 months|year))?|Booked for [^*\\n]+|Trending on RM|You\\/Your Network's Favorites|Most Booked|Private Dining Options|Other Group Dining Options"
const DISCOVERY_PAGE_RE = new RegExp(`\\*\\*(?:${PAGE_HEADERS})\\*\\*`)

// A line that is a header and nothing else: the page's, and a group ask's "Favorites"
// block (10/21), which on its own names no page.
const DISCOVERY_HEADER_LINE_RE = new RegExp(`^\\*\\*(?:${PAGE_HEADERS}|Favorites)\\*\\*$`)

/** Whether an assistant reply is RM's Discovery page in the app: it carries the page's headers. */
export const isDiscoveryPageText = text => DISCOVERY_PAGE_RE.test(String(text || ''))

/**
 * A Discovery page's text between two rows, cut into the blocks the chat draws:
 * each category header opens its own, marked `header`, so the chat gives it room
 * above (the agent-discovery-header class) where a bare line break left
 * "**Trending on RM**" sitting on the card before it (Kapil, 10/22). The lines
 * under a header (the numeral the next card takes, a closing question) stay in
 * its block.
 *
 * @param {string} text
 * @returns {{ text: string, header: boolean }[]}
 */
export const splitDiscoveryBlocks = text => {
  const blocks = []
  String(text || '').split('\n').forEach(line => {
    const header = DISCOVERY_HEADER_LINE_RE.test(line.trim())
    const last = blocks[blocks.length - 1]
    if (header || !last) blocks.push({ text: line, header })
    else last.text += `\n${line}`
  })

  return blocks.map(b => ({ text: b.text.trim(), header: b.header })).filter(b => b.text)
}

export const formatMarkdown = (text, { preferredIconSvg = '' } = {}) => {
  if (!text) return ''

  const markdownHtml = escapeHtml(text)
    .replace(/\{\{small:([^}]+)\}\}\n?/g, '<span class="agent-small">$1</span>')
    // {{add-favorite:Name|City}} — an OpenAI suggestion that is not on RM has no
    // heart to tap; this link asks the agent to add + favorite it (RM-288).
    .replace(/\{\{add-favorite:([^|}]+)\|([^}]+)\}\}/g, '<button type="button" class="agent-add-favorite" data-name="$1" data-city="$2">Add to your favorites</button>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/~~(.+?)~~/g, '<s>$1</s>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
    .replace(/\[Preferred\]/g, preferredIconSvg)
    .replace(/\[([^\]]+)\]\(\/submit-request\/([^)]+)\)/g, '<a href="/submit-request/$2" class="agent-submit-cta" role="button">$1</a>')
    .replace(/\[([^\]]+)\]\(\/r\/([^)]+)\)/g, '<a href="/r/$2" class="agent-restaurant-link">$1</a>')
    .replace(/\[([^\]]+)\]\(\/info\/([^)]+)\)/g, '<a href="/info/$2" class="agent-info-link">$1</a>')
    .replace(/\[([^\]]+)\]\((\/[^)]+)\)/g, '<a href="$2" class="agent-internal-link">$1</a>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="agent-external-link">$1</a>')
    .replace(/\n/g, '<br>')

  return linkifyUrls(markdownHtml)
}
